import _slicedToArray from "/Users/andrecruz/Work/moxy/moxy/react-promiseful/demo/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import _objectSpread from "/Users/andrecruz/Work/moxy/moxy/react-promiseful/demo/node_modules/@babel/runtime/helpers/esm/objectSpread";
import _isEqual from "lodash/isEqual";
import { useEffect, useReducer, useMemo, useRef } from 'react';
import pDelay from 'delay';
import { resolvePromise } from './promise';
import selectObjectValueBasedOnState from './util/select-object-value';

var reducer = function reducer(state, action) {
  var newState;

  switch (action.type) {
    case 'reset':
      newState = {
        status: 'none',
        value: undefined,
        withinThreshold: undefined
      };
      break;

    case 'pending':
      {
        newState = {
          status: 'pending',
          value: undefined,
          withinThreshold: action.payload
        };
        break;
      }

    case 'rejected':
      newState = _objectSpread({}, state, {
        status: 'rejected',
        value: action.payload
      });
      break;

    case 'fulfilled':
      newState = _objectSpread({}, state, {
        status: 'fulfilled',
        value: action.payload
      });
      break;

    /* istanbul ignore next */

    default:
      throw new Error('Unknown action type');
  } // Keep the same state reference if they are strictly equal to avoid rerenders


  return _isEqual(newState, state) ? state : newState;
};

var getInitialState = function getInitialState(promise, options) {
  return {
    status: !promise ? 'none' : 'pending',
    value: undefined,
    withinThreshold: !promise ? undefined : options.thresholdMs > 0
  };
};

var useResolvePromise = function useResolvePromise(promise, dispatch, options) {
  var thresholdMs = options.thresholdMs;
  useEffect(function () {
    // Short-circuit if there's no promise
    if (!promise) {
      dispatch({
        type: 'reset'
      });
      return;
    }

    var cancelDelay; // If there's no delay, set status to 'pending' right away
    // Otherwise wait for the specified elay

    if (thresholdMs > 0) {
      dispatch({
        type: 'pending',
        payload: true
      });
      cancelDelay = resolvePromise(pDelay(thresholdMs), {
        then: function then() {
          return dispatch({
            type: 'pending',
            payload: false
          });
        }
      });
    } else {
      dispatch({
        type: 'pending',
        payload: false
      });
    }

    var cancelResolve = resolvePromise(promise, {
      then: function then(value) {
        return dispatch({
          type: 'fulfilled',
          payload: value
        });
      },
      catch: function _catch(err) {
        return dispatch({
          type: 'rejected',
          payload: err
        });
      },
      finally: function _finally() {
        cancelDelay && cancelDelay();
      }
    }, true);
    return function () {
      // Cancel delay & cancel resolving promise
      cancelResolve();
      cancelDelay && cancelDelay();
    };
  }, [promise]);
};

var useNotifySettled = function useNotifySettled(state, options) {
  var onSettle = options.onSettle,
      onSettleDelayMs_ = options.onSettleDelayMs;
  var onSettleDelayMs = useMemo(function () {
    if (typeof onSettleDelayMs_ === 'number') {
      return {
        fulfilled: onSettleDelayMs_,
        rejected: onSettleDelayMs_
      };
    }

    return _objectSpread({
      fulfilled: 0,
      rejected: 0
    }, onSettleDelayMs_);
  }, [onSettleDelayMs_]);
  var stateRef = useRef();
  useEffect(function () {
    // Short-circuit if there's no callback
    if (!onSettle) {
      return;
    } // Short-circuit if state hasn't changed


    if (stateRef.current === state) {
      return;
    }

    stateRef.current = state; // Skip if the promise is not settled

    if (state.status !== 'fulfilled' && state.status !== 'rejected') {
      return;
    }

    var timeoutDelay = selectObjectValueBasedOnState(onSettleDelayMs, state, 0);

    if (timeoutDelay > 0) {
      var timeoutId = setTimeout(function () {
        return onSettle(state);
      }, timeoutDelay);
      return function () {
        return clearTimeout(timeoutId);
      };
    }

    onSettle(state);
  }, [state, onSettle, onSettleDelayMs]);
};

var usePromiseState = function usePromiseState(promise, options) {
  options = useMemo(function () {
    return _objectSpread({
      thresholdMs: 0,
      onSettleDelayMs: 0,
      onSettle: undefined,
      statusMap: undefined
    }, options);
  }, [options]);
  var initialState = useMemo(function () {
    return getInitialState(promise, options);
  });

  var _useReducer = useReducer(reducer, initialState),
      _useReducer2 = _slicedToArray(_useReducer, 2),
      state = _useReducer2[0],
      dispatch = _useReducer2[1]; // Observe promise and update the state accordingly, respecting `thresholdMs`


  useResolvePromise(promise, dispatch, options); // Call `onSettled` whenever the promise settles, respecting `onSettleDelayMs`

  useNotifySettled(state, options); // Return the state, transforming it according to `statusMap`

  return useMemo(function () {
    return _objectSpread({}, state, {
      status: selectObjectValueBasedOnState(options.statusMap, state, state.status)
    });
  }, [state, options.statusMap]);
};

export default usePromiseState;